import { useState } from 'react';
import { Chip } from '@mui/material';
import {
  CasBaseDialog,
  CasDisplayField,
  CasLoaderWithBackdrop,
  CasStackRow,
  CasTabsView,
} from 'src/compass/shared/components';
import { DateFormat, formatDate } from 'src/compass/shared/util/casMoment';
import { useRetrieveDetailsById } from 'src/compass/shared/hooks/reactQuery/useRetrieveDetailsById';
import { AccountsTab, OwnersTab } from 'src/compass/application';
import { Colors } from 'src/compass/shared/util/Colors';
import RenderTouchpointFrequency, { useTouchpointCache } from './RenderTouchpointFrequency';
import ContactsTab from './ContactsTab';

interface IProps {
  id: number;
  onClose: (dataUpdated?: boolean) => void;
}

export default function TouchpointDialog({ id, onClose }: IProps) {
  const { updateTouchpointDetails } = useTouchpointCache();
  const [dataUpdated, setDataUpdated] = useState<boolean>(false);

  const { data, isFetching, isError } = useRetrieveDetailsById({
    entity: 'Utilities/EntityTouchpoint',
    id,
  });

  if (isFetching) return <CasLoaderWithBackdrop />;
  if (isError) throw new Error('Error retrieving data from the server.');

  const tabList = [
    { label: 'Contacts', children: <ContactsTab data={data?.contacts} /> },
    { label: 'Accounts', children: <AccountsTab entityId={id} readOnly /> },
    { label: 'Owners', children: <OwnersTab entityId={id} readOnly /> },
  ];

  return (
    <CasBaseDialog
      fullScreen
      maxWidth="xl"
      open
      onClose={() => onClose(dataUpdated)}
      title={data && `Entity: ${data?.name}`}
      showCloseIcon
    >
      <CasStackRow>
        <CasDisplayField
          label="Next Touchpoint"
          value={formatDate(data?.nextTouchpointDate, DateFormat.MDY)}
        />
        <CasDisplayField
          label="Frequency"
          value={
            <RenderTouchpointFrequency
              id={id}
              defaultValue={data.touchpointFrequency}
              onUpdated={(nextTouchpointDate) => {
                updateTouchpointDetails(id, nextTouchpointDate);
                setDataUpdated(true);
              }}
            />
          }
        />
        <CasDisplayField
          label="Last Touchpoint"
          value={formatDate(data?.lastTouchpointDate, DateFormat.MDY)}
        />
      </CasStackRow>
      <CasDisplayField
        label="Risk Managers"
        value={
          <CasStackRow>
            {data?.riskManagerNames?.map((riskManager: string) => (
              <Chip label={riskManager} sx={{ backgroundColor: Colors.Present, color: 'black' }} />
            ))}
          </CasStackRow>
        }
      />
      <CasTabsView tabList={tabList} />
    </CasBaseDialog>
  );
}
